import { DataBlockDictionaryDto } from '@lmt/shared-types';
import { DataBlockDictionaryItemDto } from '@lmt/shared-types';
import { CheckboxGroup } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckboxGroup';
import { ComboBoxInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/ComboBoxInput2';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input as CommonInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { RadioButtonGroup } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButtonGroup';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultProps, DictionaryType, MandatoryProps } from './types';

function dictionaryLabelSelector(item: DataBlockDictionaryItemDto | string) {
  return typeof item === 'string' ? item : item?.value;
}

type DictionaryInputType = MandatoryProps &
  Partial<DefaultProps> & {
    /*
    Actually fieldValue should be one of: string | number | (string|number)[]
    String represents custom value, numbers represent dictionary values
    */
    fieldValue: unknown;
    isFieldValueLoaded: boolean;
    setValue?: (key: string, value: string | null | undefined) => void;
    setError?: (key: string, error: string) => void;
    isOnlyView: boolean;
    // for hardcoded fields we could pass dictionaryTitle via this prop
    dictionaryTitle?: string | undefined | null;
    dictionary?: DataBlockDictionaryDto | null;
    isErrorVisible: boolean;
  };

import styles from '../UIEditorBlocks.module.scss';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { NS_DataBlockFields } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import {
  useActiveDictionaryValuesById,
  useDictionaryByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { InputDefaultProps } from './InputDefaultConfig';
import { isEmpty } from '@lmt/unicorn-shared/app/components/measurements/copy-datablock-field/common';

/*
  This value represents that CustomOption is chosen for RadioGroupWithCustomOption or CheckboxGroupWithCustomOption.
 */
const customOptionIntValue = -2;
// if you want to change the height value of one option,
// make sure that you have changed all the necessary css styles in these files  as well
// (RadioButton.module.scss, CheckBox.module.scss)
const singleOptionHeight = 26;

export const DictionaryInput: React.FC<DictionaryInputType> = React.memo(
  function DictionaryInput(inputProps: DictionaryInputType) {
    const props = {
      ...InputDefaultProps,
      ...inputProps,
    };
    const i18n = useTranslation();
    const i18nDictionary = useTranslation(NS_DataBlockFields);
    const dictionaryByTitle = useDictionaryByTitle(props.dictionaryTitle);
    const dictionaryId = dictionaryByTitle?.id ?? props.dictionary?.id;

    const dictionaryValues = useActiveDictionaryValuesById(
      dictionaryId,
      props.fieldValue,
    );

    /*
    Contains a Dictionary of Dictionary key and values.
    Only not-deleted dictionary items are present.
    Also add initially selected dictionary value is present even if it was deleted.
     */
    const activeDictionaryValues: Record<number, DataBlockDictionaryItemDto> =
      useMemo(() => {
        if (!dictionaryValues.data) {
          return {};
        }

        const data = dictionaryValues.data ?? [];
        const values = data.map((x) => ({
          ...x,
          value: i18nDictionary.t(x.value as any),
        }));
        const result: Record<number, DataBlockDictionaryItemDto> = {};
        values.forEach((x) => {
          result[x.id] = x;
        });
        return result;
      }, [dictionaryValues.data]);

    const arrayValues: unknown[] = useMemo(
      () =>
        Array.isArray(props.fieldValue)
          ? props.fieldValue
          : isEmpty(props.fieldValue)
            ? []
            : [props.fieldValue],
      [props.fieldValue],
    );

    const selectedCustomOption = useMemo(
      () => (arrayValues.find((x) => typeof x === 'string') as string) ?? '',
      [arrayValues, props.fieldValue],
    );
    /* this one includes '-2' value if custom values should be selected */
    const selectedValuesForUIControls: number[] = useMemo(
      () =>
        arrayValues
          .filter((x) => !isEmpty(x))
          .map((x) => {
            if (typeof x === 'string') {
              return customOptionIntValue;
            } else {
              return x as number;
            }
          }),
      [arrayValues],
    );
    const [customOption, setCustomOption] =
      useState<string>(selectedCustomOption);
    useEffect(() => {
      if (selectedCustomOption !== customOption) {
        setCustomOption(selectedCustomOption);
      }
    }, [selectedCustomOption]);
    const customOptionChangedHandler = useCallback(
      (newValue: string) => {
        setCustomOption(newValue);
        let newSelectedValues = selectedValuesForUIControls.map((x) => {
          return x === customOptionIntValue ? newValue : x;
        });

        if (
          props.dictionaryType ===
            DictionaryType.CheckboxGroupWithCustomOption &&
          !newValue
        ) {
          newSelectedValues = newSelectedValues.filter((x) => x);
        }

        props.setValue?.(
          props.dataKey,
          props.dictionaryType === DictionaryType.RadioGroupWithCustomOption ||
            props.dictionaryType ===
              DictionaryType.CheckboxGroupWithCustomOption
            ? (newSelectedValues as any)
            : (newSelectedValues[0] as any),
        );
      },
      [selectedValuesForUIControls],
    );
    const comboboxCustomOptionChangedHandler = useCallback(
      (newValue: string) => {
        setCustomOption(newValue);
        const value = newValue?.trim();
        props.setValue?.(props.dataKey, value ? ([value] as any) : null);
      },
      [],
    );

    const dropDownValues: DataBlockDictionaryItemDto[] = useMemo(() => {
      let result = Object.keys(activeDictionaryValues).map((key) => ({
        id: parseInt(key),
        value: activeDictionaryValues[key as any]?.value,
        isDeleted: false,
        dictionaryId: dictionaryId ?? 0,
        manualOrderPosition:
          activeDictionaryValues[key as any]?.manualOrderPosition,
      }));

      if ((dictionaryByTitle ?? props.dictionary)?.manualOrdering) {
        result = result.sort(
          (a, b) => a?.manualOrderPosition - b?.manualOrderPosition,
        );
      } else {
        result = result.sort((a, b) => a?.value?.localeCompare(b?.value));
      }

      if (
        props.dictionaryType === DictionaryType.CheckboxGroupWithCustomOption ||
        props.dictionaryType === DictionaryType.RadioGroupWithCustomOption
      ) {
        result.unshift({
          id: customOptionIntValue,
          value: customOption,
          isDeleted: false,
          dictionaryId: dictionaryId ?? 0,
          manualOrderPosition: 1,
        });
      }

      if (
        props.dictionaryType === DictionaryType.Combobox &&
        !props.isMandatory
      ) {
        // #43235: Add 'Not Selected' option to non-mandatory Combobox
        result.unshift(null!);
      }
      return result;
    }, [
      activeDictionaryValues,
      props.dictionaryType,
      customOption,
      props.isMandatory,
    ]);

    const displayText = useMemo(() => {
      if (!props.isFieldValueLoaded) return '';

      if (!dictionaryValues.data)
        // dictionaries are not yet fetched
        return '';

      if (arrayValues.length === 0) return '-';

      const values = arrayValues.map((x) =>
        typeof x === 'number' ? activeDictionaryValues[x]?.value : x,
      );

      const sortedValues = [...values].sort((a: any, b) => a.localeCompare(b));

      return sortedValues.join(', ');
    }, [arrayValues, activeDictionaryValues, !!dictionaryValues.data]);

    const singleSelectionOptionChanged = useCallback(
      (option: DataBlockDictionaryItemDto | null) => {
        if (option === null) {
          props.setValue?.(props.dataKey, undefined);
        } else if (option?.id === customOptionIntValue) {
          props.setValue?.(props.dataKey, option?.value);
        } else {
          props.setValue?.(props.dataKey, option?.id as any);
        }
      },
      [],
    );

    const multiSelectionOptionChanged = useCallback(
      (options: DataBlockDictionaryItemDto[]) => {
        props.setValue?.(
          props.dataKey,
          options.map((x) => {
            return x.id === customOptionIntValue ? x.value : x.id;
          }) as any,
        );
      },
      [],
    );

    const error = useMemo(() => {
      if (!props.isMandatory) {
        return null;
      }
      if (!selectedValuesForUIControls[0]) {
        return i18n.t('editor.errors.option_is_not_selected');
      }

      if (
        selectedValuesForUIControls.includes(customOptionIntValue) &&
        !customOption
      ) {
        return i18n.t('editor.errors.empty_field');
      }
      return null;
    }, [selectedValuesForUIControls, props.isMandatory, !!customOption]);

    useEffect(() => {
      props.setError?.(props.dataKey, error ?? '');
    }, [error]);

    if (props.isOnlyView) {
      return (
        <div className={styles.text} data-test-id="field-value">
          {displayText}
        </div>
      );
    }
    if (!props.isEditable) {
      return <CommonInput readOnly={true} value={displayText} />;
    }

    const defaultProps = {
      error: props.isErrorVisible,
      errorText: props.isErrorVisible ? (error ?? '') : '',
    };

    if (
      props.dictionaryType === DictionaryType.DropDown ||
      props.dictionaryType === undefined
    ) {
      return (
        <DropDownInput
          useIdFunctionAsValue={true}
          required={props.isMandatory}
          variant="formInput"
          enableSearch={!!dropDownValues && dropDownValues.length > 7}
          useVirtualization={!!dropDownValues && dropDownValues.length > 200}
          {...defaultProps}
          options={dropDownValues}
          idFunction={'id'}
          getOptionLabel={'value'}
          /* sometimes in UI tests (#39331) the value is saved before dictionary items are loaded. This saves the value in text form, not as Id */
          disabled={dictionaryValues.isLoading}
          value={selectedValuesForUIControls?.[0] ?? null}
          onValueChanged={singleSelectionOptionChanged}
        />
      );
    }

    if (props.dictionaryType === DictionaryType.Combobox) {
      // #43235: Add 'Not Selected' option to non-mandatory Combobox
      const selectedValue = isEmpty(props.fieldValue)
        ? ''
        : selectedValuesForUIControls?.[0];
      let selectedValueString = selectedValue?.toString();
      if (selectedValue === customOptionIntValue) {
        selectedValueString = customOption;
      } else {
        if (typeof selectedValue === 'number') {
          selectedValueString = activeDictionaryValues[selectedValue]?.value;
        }
      }

      return (
        <ComboBoxInput
          {...defaultProps}
          /* we always pass required=false to fix #43160: Removed value is displayed in the mandatory combobox
          (if required=true and you erase the ComboBox value via Backspace button, `empty` value doesn't trigger onChange)
          */
          required={false}
          options={dropDownValues}
          enableSearch={!!dropDownValues && dropDownValues.length > 7}
          useVirtualization={!!dropDownValues && dropDownValues.length > 200}
          getOptionLabel={dictionaryLabelSelector}
          idFunction={(x) =>
            // this is to solve #43253: Selected combobox value is not highlighted in the list
            x === null || x === undefined
              ? '$#@'
              : typeof x === 'string'
                ? x
                : x?.value
          }
          value={selectedValueString}
          variant={'formInput'}
          /* sometimes in UI tests (#39331) the value is saved before dictionary items are loaded. This saves the value in text form, not as Id */
          disabled={dictionaryValues.isLoading}
          onValueChanged={(value) => {
            if (value === null || value === undefined || value === '') {
              setCustomOption('');
              singleSelectionOptionChanged(null);
            } else if (dropDownValues.includes(value as any)) {
              if ((value as any).id === selectedValue) {
                return;
              }
              setCustomOption('');
              singleSelectionOptionChanged(value as any);
            } else {
              comboboxCustomOptionChangedHandler(value! as any);
            }
          }}
        />
      );
    }

    const calculatedHeight = useMemo(() => {
      return props.visibleOptionsNumber
        ? props.visibleOptionsNumber * singleOptionHeight
        : undefined;
    }, [props.visibleOptionsNumber]);

    if (props.dictionaryType === DictionaryType.RadioGroup)
      return (
        <RadioButtonGroup
          {...defaultProps}
          visibleOptionsHeight={calculatedHeight}
          options={dropDownValues}
          valueField={'id'}
          labelField={'value'}
          value={selectedValuesForUIControls?.[0] ?? null}
          onSelectedOptionChanged={singleSelectionOptionChanged}
          required={props.isMandatory}
        />
      );

    if (props.dictionaryType === DictionaryType.RadioGroupWithCustomOption)
      return (
        <RadioButtonGroup
          {...defaultProps}
          visibleOptionsHeight={calculatedHeight}
          options={dropDownValues}
          valueField={'id'}
          labelField={'value'}
          editableFieldValue={customOptionIntValue}
          onEditableFieldChange={customOptionChangedHandler}
          value={selectedValuesForUIControls?.[0] ?? null}
          onSelectedOptionChanged={singleSelectionOptionChanged}
          required={props.isMandatory}
          isMultiline={props.isMultiline}
        />
      );

    if (props.dictionaryType === DictionaryType.CheckboxGroup)
      return (
        <CheckboxGroup
          {...defaultProps}
          visibleOptionsHeight={calculatedHeight}
          options={dropDownValues}
          valueField={'id'}
          labelField={'value'}
          selectedValues={selectedValuesForUIControls}
          onSelectedOptionChanged={multiSelectionOptionChanged}
        />
      );

    if (props.dictionaryType === DictionaryType.CheckboxGroupWithCustomOption)
      return (
        <CheckboxGroup
          {...defaultProps}
          visibleOptionsHeight={calculatedHeight}
          options={dropDownValues}
          valueField={'id'}
          labelField={'value'}
          selectedValues={selectedValuesForUIControls}
          editableFieldValue={customOptionIntValue}
          onEditableFieldChange={customOptionChangedHandler}
          onSelectedOptionChanged={multiSelectionOptionChanged}
          isMultiline={props.isMultiline}
        />
      );

    assertNever(props.dictionaryType);
  },
);
